import { Map, Feature, View, MapBrowserEvent } from 'ol';
import { Style, Stroke, Fill, Circle } from 'ol/style';
import { GeoJSON } from 'ol/format';
import { Geometry, Point } from 'ol/geom';
import { OSM, XYZ, Vector as VectorSource } from 'ol/source';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { fromLonLat, get as getProjection } from 'ol/proj';
import { Select } from 'ol/interaction';
import { SelectEvent } from 'ol/interaction/Select';
import { click } from 'ol/events/condition';
import { bbox as boundingBox } from 'ol/loadingstrategy';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import ProfileStyle from 'ol-ext/style/Profile';
import ProfileControl from 'ol-ext/control/Profile'
import tracks from './prepare/tracks.json'

proj4.defs('EPSG:21781',
    '+proj=somerc +lat_0=46.95240555555556 +lon_0=7.439583333333333 +k_0=1 ' +
    '+x_0=600000 +y_0=200000 +ellps=bessel ' +
    '+towgs84=674.4,15.1,405.3,0,0,0,0 +units=m +no_defs');
register(proj4);

const CH_LV03 = getProjection('EPSG:21781')!;
const WGS84 = getProjection('EPSG:4326')!;
const Mercator = getProjection('EPSG:3857')!;
const OSMLayer = new TileLayer({
    source: new OSM()
});
const SwisstopoLayer = new TileLayer({
    source: new XYZ({ url: 'https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.pixelkarte-farbe/default/current/3857/{z}/{x}/{y}.jpeg' })
});

declare global {
    interface HTMLDivElement {
        data?: Map
    }
}

const tagFilter: string | null = (() => {
    if (globalThis.location.hash.includes('#tag')) {
        return decodeURIComponent(globalThis.location.hash.replace('#tag=', ''));
    }
    return null;
})();
const urlFilter: string | null = (() => {
    if (globalThis.location.hash.includes('#id')) {
        return decodeURIComponent(globalThis.location.hash.replace('#id=', ''));
    }
    return null;
})();

const mapLayer = document.getElementById("map") as HTMLDivElement;
const backgroundLayer = document.getElementById("backgroundLayer") as HTMLImageElement;
const hiddenBackgroundLayer = document.getElementById("hiddenBackgroundLayer") as HTMLImageElement;
const popupLayer = document.getElementById('info')!;

const Filters = {
    Wandern: "Wandern",
    Bike: "Bike",
    Velo: "Velo",
    Skitour: "Skitour",
    Swim: "Swim",
    Plan: "Plan",
    Other: "Other"
};

const TrackStyle = {
    Highlight: "Highlight",
    Background: "Background",
    Normal: "Normal",
    Hide: "Hide",
};

function getFilter(id: number) {
    switch (id) {
        case 1774:
            return Filters.Wandern;
        case 1773:
            return Filters.Bike;
        case 1779:
            return Filters.Velo;
        case 79387:
            return Filters.Skitour;
        case 77273:
            return Filters.Plan;
        case 160146:
            return Filters.Swim;
    }
    return Filters.Other;
}

function getStyle(filterId: number, trackStyle: string): Style {
    const trackWidth = function () {
        switch (trackStyle) {
            case TrackStyle.Highlight:
                return 4;
            case TrackStyle.Background:
                return .8;
            default:
                return 3;
        }
    }();
    let lineColor = function () {
        switch (getFilter(filterId)) {
            case Filters.Wandern:
                return '#00f';
            case Filters.Bike:
                return '#a2294b';
            case Filters.Velo:
                return '#000';
            case Filters.Skitour:
                return '#555';
            case Filters.Swim:
                return '#93b';
            case Filters.Plan:
                return '#cc0';
        }
        return "#fff";
    }();

    if (trackStyle == TrackStyle.Hide) {
        lineColor = '#00000000';
    }

    const stroke = new Stroke({
        color: lineColor,
        width: trackWidth
    });
    if (trackStyle == TrackStyle.Highlight) {
        return new ProfileStyle({
            stroke: stroke,
            fill: new Fill({ color: [25, 25, 25, .4] }),
            scale: .1
        });
    }
    return new Style({ stroke: stroke });
}

function createLayerFromId(trackID: number, filterId: number): VectorLayer<VectorSource> {
    const url = "https://map.simonste.ch/data/" + trackID + ".geojson"
    return createLayer(url, filterId);
}

function createLayer(path: string, filterId: number): VectorLayer<VectorSource> {
    let style = TrackStyle.Normal;
    if (urlFilter != null) { style = TrackStyle.Background; }
    const vectorSource = new VectorSource({
        format: new GeoJSON(),
        loader: function (extent, resolution, projection) {
            let xhr = new XMLHttpRequest();
            xhr.open('GET', path);
            let onError = function () {
                vectorSource.removeLoadedExtent(extent);
            }
            xhr.onerror = onError;
            xhr.onload = function () {
                if (xhr.status == 200 && xhr.responseText.startsWith('{')) {
                    let feature = vectorSource.getFormat()!.readFeatures(xhr.responseText, {
                        dataProjection: WGS84,
                        featureProjection: Mercator
                    })[0] as Feature<Geometry>;

                    if (tagFilter == null ||
                        feature.getProperties().tags == undefined ||
                        feature.getProperties().tags.includes(tagFilter)) {

                        vectorSource.addFeature(feature);
                        if (feature.getId() == urlFilter) {
                            const mapEvent = new MapBrowserEvent('select', mapLayer.data!, new UIEvent('select'));
                            highlight.dispatchEvent(new SelectEvent('select', [feature], [], mapEvent));
                        }
                    }
                } else {
                    console.log('failed to load ' + path)
                    onError();
                }
            }
            xhr.send();
        },
        strategy: boundingBox,
    });
    return new VectorLayer({
        source: vectorSource,
        style: getStyle(filterId, style)
    });
}

let highlight = new Select({
    condition: click,
});
let tagged = new Select({
});

function get_duration(data: any, filterId: number) {
    let min = 0;
    if (data.tracked_time) {
        min = data.tracked_time.toFixed(0)
    } else {
        switch (getFilter(filterId)) {
            case Filters.Wandern:
                min = data.meta.walking;
                break;
            case Filters.Bike:
                min = data.meta.mountainBiking;
                break;
            case Filters.Velo:
                min = data.meta.biking;
                break;
            case Filters.Skitour:
                min = data.meta.snowshoe_trekking;
                break;
            default:
                break;
        }
    }
    if (min > 60) {
        return Math.floor(min / 60) + 'h ' + Math.floor(min % 60) + 'min';
    }
    return min + 'min';
}

function popup(selected_track, trackId: number) {
    let trackName = selected_track.name;
    let date = selected_track.userdate;

    let length = (selected_track.meta.length / 1000).toFixed(2) + " km";
    let up = "↑ " + (selected_track.meta.totalup).toFixed(0) + " m";
    let down = "↓ " + (selected_track.meta.totaldown).toFixed(0) + " m";
    let duration = "⏱ " + get_duration(selected_track, selected_track.filter.id);

    popupLayer.getElementsByClassName('title')[0].innerHTML = "<a href='/?#id=" + trackId + "'>" + trackName.substring(7) + "</a> @" + date;
    popupLayer.getElementsByClassName('meta')[0].innerHTML = "<table><tr><td>" + length + "</td><td>" + duration + "</td></tr><tr><td>" +
        up + "</td><td>" + down + "</td></tr></table>";


    let tags = selected_track.tags.toString().split(",").map(function (tag: string) {
        return "<a href='/?#tag=" + tag + "'>" + tag + "</a>";
    });
    popupLayer.getElementsByClassName('tags')[0].innerHTML = tags;
}

function setToBackgroundExcept(filter: string) {
    popupLayer.style.display = 'none'
    mapLayer.data!.getLayers().getArray().forEach(function (layer) {
        if (layer instanceof VectorLayer && layer.getSource().getFeatures) {
            layer.getSource().getFeatures().forEach(function (f) {
                if (getFilter(f.values_.filter.id) == filter || filter == Filters.Other) {
                    f.setStyle(getStyle(f.values_.filter.id, TrackStyle.Normal));
                } else {
                    f.setStyle(getStyle(f.values_.filter.id, TrackStyle.Background));
                }
            })
        }
    });
}

function toggleBackgroundLayer(): void {
    let osmLayer = mapLayer.data!.getLayers().getArray().at(1)!;
    osmLayer.setVisible(!osmLayer.getVisible());

    [backgroundLayer.src, hiddenBackgroundLayer.src] = [hiddenBackgroundLayer.src, backgroundLayer.src]

    console.log("osmLayer " + osmLayer.getVisible());
}

function drawMap() {
    let map = new Map({
        target: 'map',
        layers: [SwisstopoLayer, OSMLayer],
        view: new View({
            center: fromLonLat([8.25, 46.75]),
            zoom: 8,
        })
    });
    mapLayer.data = map;

    if (tagFilter != null) {
        const mapLayer = document.getElementById("tag") as HTMLDivElement;
        mapLayer.style.display = 'block'
        mapLayer.innerHTML = tagFilter + " <a href='/'>x</a>";
    }

    for (let track of tracks.items) {
        const filterId: number = track['filter']['id'];
        if ([Filters.Wandern, Filters.Bike, Filters.Velo, Filters.Skitour, Filters.Swim].includes(getFilter(filterId))) {
            map.addLayer(createLayerFromId(track['id'], filterId));
        }
    }

    const profile = new ProfileControl({
        target: popupLayer.getElementsByClassName('profileChart')[0]
    });
    map.addControl(profile);

    let pt = new Feature(new Point([0, 0]));
    let overlay = new VectorLayer({ source: new VectorSource() });
    overlay.getSource()!.addFeature(pt);
    overlay.setMap(map);
    profile.on(["over", "out"], function (e) {
        if (!pt) return;
        if (e.type == "over") {
            // Show point at coord
            pt.setGeometry(new Point(e.coord));
            pt.setStyle(new Style({
                image:
                    new Circle({
                        radius: 6,
                        fill: new Fill({ color: 'yellow' }),
                        stroke: new Stroke({ color: 'red', width: 2 })
                    })
            }));
        } else {
            pt.setStyle([]); // hide
        }
    });

    map.addInteraction(highlight);
    highlight.setHitTolerance(20);
    highlight.on('select', function (e) {
        if (e.selected.length > 0) {
            setToBackgroundExcept(Filters.Other);

            popupLayer.style.display = 'block'
            popup(e.selected[0].getProperties(), e.selected[0].getId());
            if (e.selected[0].getProperties().geometry.flatCoordinates[3] != 0) {
                // timestamp coordinate starts with 0 when available
                console.log("no elevation data");
            }

            const filterId: number = e.selected[0].getProperties().filter.id;
            e.selected[0].setStyle(getStyle(filterId, TrackStyle.Highlight));
            profile.setGeometry(e.selected[0]);
            // Zoom to feature
            map.getView().fit(e.selected[0].getGeometry()!.getExtent(), { padding: [50, 50, 50, 50] });
        } else {
            popupLayer.style.display = 'none'
            const filterId: number = e.deselected[0].getProperties().filter.id;
            e.deselected[0].setStyle(getStyle(filterId, TrackStyle.Normal));
        }
    });
    tagged.on('show', function (e) {
        if (e.selected.length > 0) {
            const filterId: number = e.selected[0].values_.filter.id;
            e.selected[0].setStyle(getStyle(filterId, TrackStyle.Normal));
        }
    });
}

drawMap();

if (backgroundLayer) {
    backgroundLayer.addEventListener('click', function () { toggleBackgroundLayer() });
}

for (let filter of [Filters.Other, Filters.Velo, Filters.Bike, Filters.Wandern, Filters.Skitour, Filters.Swim]) {
    let button = document.getElementById(filter.toLowerCase())
    if (button) {
        button.addEventListener('click', function () { setToBackgroundExcept(filter) });
    }
}
